import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { type DateValue, parseDuration, today } from '@internationalized/date';
import { createFileRoute, Link } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { flattenData, useActiveRealmAPIContext, useFlatData } from '@interxfi/api-client';
import {
  useExcIceGsmV1InstrumentQueryServiceInstrumentList,
  useIxcAssetsV1AssetExceptionQueryServiceAssetExceptionList,
} from '@interxfi/api-client/generated/hooks';
import { useIxcAssetsClearingV1MoneyMarketInternalStatsQueryServiceMoneyMarketInternalStatsList } from '@interxfi/api-client/generated/hooks/ixc-assets-clearing-v1-money-market-internal-stats-query-service-money-market-internal-stats-list';
import { useIxcAssetsV1AssetQueryServiceAssetGet } from '@interxfi/api-client/generated/hooks/ixc-assets-v1-asset-query-service-asset-get';
import { useIxcAssetsV1MoneyMarketStatsQueryServiceMoneyMarketStatsList } from '@interxfi/api-client/generated/hooks/ixc-assets-v1-money-market-stats-query-service-money-market-stats-list';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { useIsWriteAllowed } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import type { DateAvailabilityDeterminer } from '@interxfi/design-system/components/input';
import {
  buildDatePickerButtonRenderer,
  DatePickerButtonRenderer,
} from '@interxfi/design-system/components/input/date-picker-input/activators/button/date-picker-button-activator';
import { DatePickerInput } from '@interxfi/design-system/components/input/date-picker-input/date-picker-input';
import { getDateAvailabilityDeterminerNotAfter } from '@interxfi/design-system/components/input/date-picker-input/util';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { ReadonlyListItemValue } from '@interxfi/design-system/components/list';
import { EVEN_LOADING_SKELETON_LIST_ITEM, type ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListItemValueType } from '@interxfi/design-system/components/list/readonly/item/value';
import { NoOpTableFilter } from '@interxfi/design-system/components/table';
import { Typography } from '@interxfi/design-system/components/typography';
import ArchiveIcon from '@interxfi/design-system/icons/react/icon-archive';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import HistoryIcon from '@interxfi/design-system/icons/react/icon-history';
import LinkIcon from '@interxfi/design-system/icons/react/icon-link';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import SvgIconRepeat from '@interxfi/design-system/icons/react/icon-repeat';
import { usePreferredTimeZone } from '@interxfi/design-system/time-zone-context';
import { useCurrentDate } from '@interxfi/design-system/util';
import {
  ExcIceGsmV1InstrumentQueryServiceInstrumentListFilterableFields,
  type ExcIceGsmV1InstrumentQueryServiceInstrumentListInstrumentListRequest,
  IxcAssetsClearingV1MoneyMarketInternalStatsQueryServiceMoneyMarketInternalStatsListFilterableFields,
  type IxcAssetsClearingV1MoneyMarketInternalStatsQueryServiceMoneyMarketInternalStatsListMoneyMarketInternalStatsListRequest,
  IxcAssetsV1AssetExceptionQueryServiceAssetExceptionListFilterableFields,
  IxcAssetsV1AssetStatus,
  IxcAssetsV1MoneyMarketStatsQueryServiceMoneyMarketStatsListFilterableFields,
  type IxcAssetsV1MoneyMarketStatsQueryServiceMoneyMarketStatsListMoneyMarketStatsListRequest,
} from '@interxfi/types';
import { AssetArchiveConfirmDialog } from '../../../../../../../features/asset/asset-archive-confirm-dialog';
import { AssetEodPriceBackfillDrawer } from '../../../../../../../features/asset/asset-eod-price-backfill-drawer';
import { AssetEventDrawer } from '../../../../../../../features/asset/asset-event';
import { getAssetPageDetailListItems } from '../../../../../../../features/asset/asset-page-detail-list-items';
import { AssetStatusPill } from '../../../../../../../features/asset/asset-status-pill';
import { EditAssetDrawer } from '../../../../../../../features/asset/edit-asset-drawer';
import { AssetHoursExceptionCreateDrawer } from '../../../../../../../features/asset/hours-exception/asset-hours-exception-create-drawer';
import { AssetHoursExceptionsTable } from '../../../../../../../features/asset/hours-exception/asset-hours-exceptions-table';
import { AssetLogoWithEdit } from '../../../../../../../features/asset/logo/asset-logo-with-edit';
import { MoneyMarketStatsHistoryDrawer } from '../../../../../../../features/asset/money-market-stats/history-drawer/money-market-stats-event-drawer';
import { getInternalMoneyMarketStatsStateListItems } from '../../../../../../../features/asset/money-market-stats/internal/internal-money-market-stats-list-items';
import { reduceLatestInternalMoneyMarketStatsList } from '../../../../../../../features/asset/money-market-stats/internal/utils';
import { getMoneyMarketStatsStateListItems } from '../../../../../../../features/asset/money-market-stats/money-market-stats-list-items';
import { useAssetEndOfDayQuote } from '../../../../../../../features/asset/quote/asset-eod-quote';
import { AssetEodQuoteListItemValue } from '../../../../../../../features/asset/quote/asset-eod-quote-list-item-value';
import { getAssetSymbol, getIsAssetEquity } from '../../../../../../../features/asset/utils';
import { MarketDataActionBanner } from '../../../../../../../features/market-data/market-data-action-banner';
import { getMarketDataQuotePriceListItem } from '../../../../../../../features/market-data/market-data-quote-list-items';
import { getIceGsmInstrumentDetailPath } from '../../../../../../../features/vendor/ice/global-security-master/instrument/ice-gsm-instrument-routing';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/asset/_asset/$assetId/_detail/')({
  head: () => getPageHead('Asset'),
  component: AssetDetail,
});

function getMenuItems(
  assetId: string,
  status: IxcAssetsV1AssetStatus | undefined,
  matchingIceInstrumentId: string | undefined,
  isClearingUser: boolean,
): EntityActionMenuConfig {
  const iceInstrumentLink = getIceGsmInstrumentDetailPath(isClearingUser ? matchingIceInstrumentId : undefined);

  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: AssetEventDrawer,
        props: { request: { assetId } },
      },
    },
    actions: isClearingUser
      ? ([
          {
            id: 'edit',
            icon: <EditIcon aria-hidden />,
            label: 'Edit',
            interaction: {
              type: 'drawer',
              drawer: { Component: EditAssetDrawer, props: { assetId } },
            },
            requiresWrite: true,
          },
          ...(status === IxcAssetsV1AssetStatus.Active
            ? ([
                {
                  id: 'archive',
                  label: 'Archive',
                  tone: 'danger-dk',
                  icon: <ArchiveIcon aria-hidden />,
                  interaction: {
                    type: 'modal',
                    modal: { Component: AssetArchiveConfirmDialog, props: { assetId } },
                  },
                  requiresWrite: true,
                },
              ] as const)
            : []),
          {
            id: 'addHoursException',
            icon: <PlusIcon aria-hidden />,
            label: 'Add Hours Exception',
            interaction: {
              type: 'drawer',
              drawer: { Component: AssetHoursExceptionCreateDrawer, props: { assetId } },
            },
            requiresWrite: true,
          },
          {
            id: 'viewIceInstrumentSource',
            icon: <LinkIcon aria-hidden />,
            label: 'View ICE Instrument',
            LinkElement: Link,
            to: iceInstrumentLink,
          },
        ] as const satisfies EntityActionMenuItem[])
      : undefined,
  };
}

function RequestBackfillNode({ date }: { date: string | null }) {
  const { assetId } = Route.useParams();
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const isWriteAllowed = useIsWriteAllowed();

  if (activeTenantIsClearing && isWriteAllowed) {
    return (
      <AssetEodPriceBackfillDrawer
        assetId={assetId}
        forDate={date || undefined}
        trigger={
          <Button affixIcon={<SvgIconRepeat aria-hidden width={16} />} aria-label="Backfill EOD Quotes" size="small" variant="light">
            Backfill Price Data
          </Button>
        }
      />
    );
  }

  return <ReadonlyListItemValue defaultRenderWhenEmpty="--" renderWhenEmpty="--" string={{ value: undefined }} />;
}

function getEodQuoteListItem(eodQuoteData: ReturnType<typeof useAssetEndOfDayQuote>, timeZone: string) {
  const { isPending, priorDayData, selectedDayData, selectedQuoteDate, setSelectedQuoteDate } = eodQuoteData;
  const now = today(timeZone);
  const dateAvailabilityDeterminer = getDateAvailabilityDeterminerNotAfter(now.add(parseDuration('P1D')).toDate(timeZone), timeZone);

  return {
    id: 'eodQuote',
    title: (
      <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
        <span>EOD Price</span>
        <DatePickerInput
          Activator={DatePickerButtonRenderer}
          dateAvailabilityDeterminer={dateAvailabilityDeterminer}
          label="Selected Date"
          name="selectedEndOfDayPriceDate"
          onChange={(e) => {
            setSelectedQuoteDate(e.target.value);
          }}
          value={selectedQuoteDate || now.toString()}
        />
      </HorizontalStack>
    ),
    padding: { bottom: '8px', top: '8px', left: '0px', right: '0px' },
    value: match({ isPending, selectedDayData, priorDayData })
      .returnType<ReadonlyListItemValueType>()
      .with({ isPending: true }, () => EVEN_LOADING_SKELETON_LIST_ITEM.value)
      .with({ selectedDayData: { eodPrice: { data: { price: P.not(P.nullish) } } } }, ({ selectedDayData: s, priorDayData: p }) => ({
        node: { value: <AssetEodQuoteListItemValue priorDayQuote={p?.eodPrice?.data?.price} selectedDayQuote={s!.eodPrice.data.price} /> },
      }))
      .otherwise(() => ({ node: { value: <RequestBackfillNode date={selectedQuoteDate} /> } })),
  } as const satisfies ReadonlyListItem;
}

function buildIceInstrumentRequest(assetId: string): ExcIceGsmV1InstrumentQueryServiceInstrumentListInstrumentListRequest {
  return {
    query: {
      filters: [
        {
          field: {
            name: ExcIceGsmV1InstrumentQueryServiceInstrumentListFilterableFields.DataAssetId,
            type: { value: assetId },
          },
        },
      ],
    },
  };
}

function buildMoneyMarketStatsListRequest(assetId: string): IxcAssetsV1MoneyMarketStatsQueryServiceMoneyMarketStatsListMoneyMarketStatsListRequest {
  return {
    query: {
      filters: [
        {
          field: {
            name: IxcAssetsV1MoneyMarketStatsQueryServiceMoneyMarketStatsListFilterableFields.AssetId,
            type: {
              value: assetId,
            },
          },
        },
      ],
    },
  };
}

function buildMoneyMarketInternalStatsListRequest(
  assetId: string,
): IxcAssetsClearingV1MoneyMarketInternalStatsQueryServiceMoneyMarketInternalStatsListMoneyMarketInternalStatsListRequest {
  return {
    query: {
      filters: [
        {
          field: {
            name: IxcAssetsClearingV1MoneyMarketInternalStatsQueryServiceMoneyMarketInternalStatsListFilterableFields.AssetId,
            type: {
              value: assetId,
            },
          },
        },
      ],
    },
  };
}

function useMoneyMarketStats(assetId: string) {
  const currentDate = useCurrentDate();
  const [selectedDate, setSelectedDate] = useState(currentDate.dateAsString);
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const { data: assetData } = useIxcAssetsV1AssetQueryServiceAssetGet({ assetId });
  const isMoneyMarket = assetData?.asset?.data?.type && 'moneyMarket' in assetData.asset.data.type;
  const {
    data: statsData,
    isPending: statsPending,
    error: statsError,
    hasNextPage: statsHasNextPage,
    fetchNextPage: statsFetchNextPage,
    isFetchingNextPage: statsIsFetchingNextPage,
  } = useIxcAssetsV1MoneyMarketStatsQueryServiceMoneyMarketStatsList(isMoneyMarket ? buildMoneyMarketStatsListRequest(assetId) : undefined);
  const {
    data: internalStatsData,
    isPending: internalStatsPending,
    error: internalStatsError,
    hasNextPage: internalStatsHasNextPage,
    fetchNextPage: internalStatsFetchNextPage,
    isFetchingNextPage: internalStatsIsFetchingNextPage,
  } = useIxcAssetsClearingV1MoneyMarketInternalStatsQueryServiceMoneyMarketInternalStatsList(
    activeTenantIsClearing && isMoneyMarket ? buildMoneyMarketInternalStatsListRequest(assetId) : undefined,
  );
  const flatStats = useFlatData(statsData, 'moneyMarketStats');
  const flatInternalStats = useMemo(
    () => reduceLatestInternalMoneyMarketStatsList(flattenData(internalStatsData, 'moneyMarketInternalStats')),
    [internalStatsData],
  );
  const hasNextPage = statsHasNextPage || internalStatsHasNextPage;
  const isFetchingNextPage = statsIsFetchingNextPage || internalStatsIsFetchingNextPage;
  const fetchNextPage = useCallback(async () => {
    if (internalStatsHasNextPage) {
      internalStatsFetchNextPage().catch();
    }

    if (statsHasNextPage) {
      statsFetchNextPage().catch();
    }
  }, [internalStatsHasNextPage, internalStatsFetchNextPage, statsHasNextPage, statsFetchNextPage]);

  const initialInternalDataHash = flatInternalStats.find((s) => s.assetId === assetId && s.date === selectedDate)?.dataHash;

  const availableDates = useMemo(() => {
    if (!isMoneyMarket) {
      return [];
    }

    return Array.from(new Set([...flatStats.map((s) => s.date), ...flatInternalStats.map((s) => s.date)]))
      .sort()
      .reverse();
  }, [flatStats, flatInternalStats, isMoneyMarket]);

  useEffect(() => {
    setSelectedDate((prevSelectedDate) => {
      if (availableDates.includes(prevSelectedDate)) {
        return prevSelectedDate;
      }

      return availableDates.length > 0 ? availableDates[0] : currentDate.dateAsString;
    });
  }, [availableDates, currentDate.dateAsString]);

  const showPending = Boolean(statsPending || internalStatsPending || statsError || internalStatsError);

  const listItems = useMemo(() => {
    if (!isMoneyMarket) {
      return undefined;
    }

    const statsItems = getMoneyMarketStatsStateListItems(flatStats.find((s) => s.date === selectedDate));
    const internalStatsItems = getInternalMoneyMarketStatsStateListItems(flatInternalStats.find((s) => s.date === selectedDate));

    return [
      statsItems.data.sevenDayYield,
      statsItems.data.nav.navDate,
      { ...statsItems.data.nav.strikeSummary, title: 'NAV Strikes' },
      ...(activeTenantIsClearing
        ? [
            internalStatsItems.data.dividendRate,
            internalStatsItems.data.monthToDateDividend,
            internalStatsItems.data.dailyDividend,
            internalStatsItems.data.shares,
          ]
        : []),
    ] as const satisfies ReadonlyListItem[];
  }, [flatStats, flatInternalStats, isMoneyMarket, activeTenantIsClearing, selectedDate]);

  return useMemo(
    () => ({
      availableDates,
      selectedDate,
      setSelectedDate,
      initialInternalDataHash,
      listItems,
      showPending,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    }),
    [availableDates, selectedDate, listItems, initialInternalDataHash, showPending, hasNextPage, isFetchingNextPage, fetchNextPage],
  );
}

const DatePickerActivatorRenderer = buildDatePickerButtonRenderer('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load asset', 'view this asset');

function AssetDetail() {
  const { assetId } = Route.useParams();
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const request = { assetId } as const;
  const { data, error, isPending } = useIxcAssetsV1AssetQueryServiceAssetGet(request);
  useErrorHandler(error, errorMessageGetter);
  const timeZone = usePreferredTimeZone();
  const hasMarketData = getIsAssetEquity(data?.asset?.data?.type);
  const showPending = Boolean(isPending || error);
  const eodQuoteData = useAssetEndOfDayQuote(hasMarketData ? assetId : undefined);
  const eodListItem = useMemo(() => getEodQuoteListItem(eodQuoteData, timeZone), [eodQuoteData, timeZone]);
  const symbol = getAssetSymbol(data?.asset?.data?.type);
  const quoteListItem = useMemo(() => getMarketDataQuotePriceListItem(symbol), [symbol]);
  const listItems = useMemo(() => getAssetPageDetailListItems(data?.asset, eodListItem, quoteListItem), [data?.asset, eodListItem, quoteListItem]);
  const { data: matchingIceAssetData } = useExcIceGsmV1InstrumentQueryServiceInstrumentList(
    activeTenantIsClearing ? buildIceInstrumentRequest(assetId) : undefined,
  );
  const matchingIceInstrumentId = matchingIceAssetData?.pages?.[0]?.instrument?.[0]?.instrumentId;
  const menuItems = useMemo(
    () => getMenuItems(assetId, data?.asset?.status, matchingIceInstrumentId, activeTenantIsClearing),
    [assetId, data?.asset?.status, matchingIceInstrumentId, activeTenantIsClearing],
  );
  const moneyMarketStatData = useMoneyMarketStats(assetId);
  const handleStatDateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      moneyMarketStatData.setSelectedDate(e.target.value);
    },
    [moneyMarketStatData.setSelectedDate],
  );

  const dateAvailabilityDeterminer: DateAvailabilityDeterminer = useCallback(
    (date: DateValue) => {
      return !moneyMarketStatData.availableDates.includes(date.toString());
    },
    [moneyMarketStatData.availableDates],
  );

  const { data: assetExceptionResponse } = useIxcAssetsV1AssetExceptionQueryServiceAssetExceptionList({
    query: {
      filters: [
        { field: { name: IxcAssetsV1AssetExceptionQueryServiceAssetExceptionListFilterableFields.DataSpecAssetsAssetId, type: { value: assetId } } },
      ],
    },
  });
  const hasExceptions = useFlatData(assetExceptionResponse, 'assetException').length > 0;

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={DEFAULT_PAGE_LAYOUT_GAP}>
          <PageHeader>
            <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
              {activeTenantIsClearing ? <AssetLogoWithEdit assetId={assetId} /> : null}
              <AssetStatusPill isPending={showPending} size="large" status={data?.asset?.status} />
            </HorizontalStack>

            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: (
                <VerticalStack gap={7}>
                  {hasMarketData ? <MarketDataActionBanner assetId={assetId} /> : null}
                  <AppReadonlyList isPending={showPending} items={listItems.general.left} />
                </VerticalStack>
              ),
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {hasExceptions ? (
        <PageLayout.Section>
          <VerticalStack gap={7}>
            <Typography.Level3>Asset Exceptions</Typography.Level3>
            <AssetHoursExceptionsTable assetId={assetId} HeaderActions={NoOpTableFilter} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {moneyMarketStatData.listItems?.length ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <HorizontalStack align="space-between" blockAlign="center" gap={2} wrap="nowrap">
              <Typography.Level3>Statistics</Typography.Level3>

              <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
                <DatePickerInput
                  Activator={DatePickerActivatorRenderer}
                  dateAvailabilityDeterminer={dateAvailabilityDeterminer}
                  label="Select Month"
                  name="money-market-stats-month"
                  onChange={handleStatDateChange}
                  pagination={{
                    hasNextPage: moneyMarketStatData.hasNextPage,
                    isFetchingNextPage: moneyMarketStatData.isFetchingNextPage,
                    fetchNextPage: moneyMarketStatData.fetchNextPage,
                    lastAvailableDate: moneyMarketStatData.availableDates[moneyMarketStatData.availableDates.length - 1],
                  }}
                  value={moneyMarketStatData.selectedDate}
                />

                <MoneyMarketStatsHistoryDrawer
                  assetId={assetId}
                  date={moneyMarketStatData.selectedDate}
                  initialInternalDataHash={moneyMarketStatData.initialInternalDataHash}
                  trigger={
                    <Button
                      affixIcon={<HistoryIcon aria-hidden width={16} />}
                      aria-label="View History"
                      disabled={isPending}
                      size="small"
                      variant="light"
                    />
                  }
                />
              </HorizontalStack>
            </HorizontalStack>

            <AppReadonlyList isPending={moneyMarketStatData.showPending} items={moneyMarketStatData.listItems} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
